import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Col, Container, Form, Row, Spinner,
} from 'react-bootstrap';
import RangeSlider from 'react-bootstrap-range-slider';
import { FaPlay } from 'react-icons/fa';
import { useDebounce } from 'use-debounce';
import { VideoModal } from 'components/pages/VideoLibraryPage/VideoModal/VideoModal';
import { api } from '../../../api';
import { Auth } from '../../../Auth/Auth';
import { Pagination } from '../../../ui/Pagination/Pagination';
import { isVideosEnabled } from '../../../utils/userActionUtils';
import { useAudioPlayer } from '../../AudioLibraryPage/useAudioPlayer';
import { useBackgrounds } from '../../Backgrounds/useBackgrounds';
import styles from '../../Characters/Characters.scss';
import { AudioPicker } from '../../Locations/AudioPicker/AudioPicker';
import { VideoPicker } from '../../Locations/VideoPicker/VideoPicker';
import { useVideoPlayer } from '../../VideoLibraryPage/useVideoPlayer';
import classes from './LocationsForm.module.scss';
import { LocationsList } from './LocationsList/LocationsList';
import { Search } from './LocationsList/Search';

const cs = classNames.bind(styles);

const pageSize = 15;

export function LocationsForm({
  onSuccess,
  bookId,
  onStopEdit,
  loading,
  storyLocationToEdit,
  limits,
  disabled,
  isFirstLocationInStory,
}) {
  const storyLocationToEditId = storyLocationToEdit?.id ?? null;
  const isNew = !storyLocationToEditId;

  const initialSearchTerm = storyLocationToEdit?.image?.alias ?? '';

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);

  const auth = new Auth();
  const user = auth.getUser();
  const userRole = user ? user.role : 'unknown';
  const videoEnabled = isVideosEnabled(user);
  const {
    currentPage,
    setCurrentPage,
    totalCount,
    backgrounds,
    isLoading,
    showOnlyUserImages,
    setShowOnlyUserImages,
  } = useBackgrounds(debouncedSearchTerm, pageSize, user?.id);

  const handleSearchChange = (newSearchTerm) => {
    setCurrentPage(1);
    setSearchTerm(newSearchTerm);
  };

  const [isDefaultLocation, setIsDefaultLocation] = useState(isFirstLocationInStory || false);
  const [title, setTitle] = useState('');
  const [formError, setFormError] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [validated, setValidated] = useState(false);
  const [isVideoModalShown, setIsVideoModalShown] = useState(false);

  const {
    audioVolume,
    setAudioVolume,
    isLoopAudio,
    setIsLoopAudio,
    isPlayingAudio,
    togglePlayAudio,
  } = useAudioPlayer(selectedAudio?.audioUrl, storyLocationToEdit?.audioSettings);

  const {
    videoVolume,
    isLoopVideo,
    setIsLoopVideo,
    allowStepActions,
    setAllowStepActions,
  } = useVideoPlayer(selectedVideo?.videoUrl, storyLocationToEdit?.videoSettings);

  useEffect(() => {
    if (storyLocationToEdit) {
      setSelectedImageId(storyLocationToEdit.backgroundId);
      setTitle(storyLocationToEdit.title);
      setIsDefaultLocation(storyLocationToEdit.isDefault);
      if (storyLocationToEdit.audioId) {
        api.get(`/v1/audio/${storyLocationToEdit.audioId}`).then((response) => {
          setSelectedAudio(response.data.audio);
        });
      }
      if (storyLocationToEdit.videoId) {
        api.get(`/v1/video/${storyLocationToEdit.videoId}`).then((response) => {
          setSelectedVideo(response.data.video);
        });
      }
    }
  }, [setAudioVolume, setIsLoopAudio, setIsLoopVideo, setAllowStepActions, storyLocationToEdit]);

  function errorAlert(error) {
    setFormError(error);
    setTimeout(() => {
      setFormError(null);
    }, 3000);
  }

  const handleImageClick = (image) => {
    setSelectedImageId(image.id);
    if (isNew) {
      setTitle(image?.label ?? '');
    }
  };

  function handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) {
      return;
    }

    const form = event.currentTarget;

    if (!selectedImageId) {
      errorAlert('Please select location from the list');
      return;
    }

    if (form.checkValidity() !== false) {
      /*
        videoVolume is not implemented yet
        DOR-8474: (iOS) Video volume controls do not function on QVBR videos
       */
      const locationUpdate = {
        title,
        backgroundId: selectedImageId,
        isDefault: isDefaultLocation,
        audioId: selectedAudio?.id ?? null,
        audioSettings: {
          volume: audioVolume,
          loop: isLoopAudio,
        },
        videoId: selectedVideo?.id ?? null,
        videoSettings: selectedVideo?.id != null ? {
          volume: videoVolume,
          loop: isLoopVideo,
          allowStepActions,
          autoplay: 1,
        } : null,
      };

      if (storyLocationToEditId || storyLocationToEditId === 0) {
        api.put(`/v1/books/${bookId}/locations/${storyLocationToEditId}`, locationUpdate)
          .then((response) => onSuccess(response.data.location))
          .catch((error) => {
            if (error) {
              errorAlert(error.response.data.error);
            }
          });
      } else {
        api.post(`/v1/books/${bookId}/locations`, locationUpdate)
          .then((response) => onSuccess(response.data.location))
          .catch((error) => {
            if (error) {
              errorAlert(error.response.data.error);
            }
          });
      }
    }

    setValidated(true);
  }

  const handleAudioPickerConfirm = (value) => setSelectedAudio(value);

  const handleVideoPickerConfirm = (value) => setSelectedVideo(value);

  const createRangeTooltip = (value) => `${value * 100}%`;

  const handleRangeChanged = (event) => setAudioVolume(event.target.value);

  const handleVideoPreviewClick = () => setIsVideoModalShown(true);

  const handleLoopChanged = (event) => {
    setAllowStepActions(!event.target.checked ? allowStepActions : 1);
    setIsLoopVideo(!event.target.checked ? 0 : -1);
  };

  const isTitleTooLong = title && title.length > Number(limits.scene_title_max.value);

  return (
    <Container className={cs(loading === false ? 'formEdit' : null, 'py-2 border-bottom')}>
      <fieldset
        disabled={!!disabled}
        style={{
          border: 0, margin: 0, padding: 0, minInlineSize: '100%',
        }}
      >
      <Row>
        <Col md={12} className="text-center mb-3">
          Story location:
        </Col>
      </Row>
      <Form
        noValidate
        validated={validated}
        onSubmit={(e) => handleSubmit(e)}
      >
        <Row>
          <Col md={12} className={cs(formError === null ? 'd-none' : 'd-block')}>
            <Alert variant="danger">
              {formError}
            </Alert>
          </Col>
        </Row>
        <Row className="mb-1">
          <Col md={12}>
            <Form.Check
              custom
              disabled={isFirstLocationInStory}
              id="isDefault"
              name="isDefault"
              checked={isDefaultLocation}
              onChange={(event) => setIsDefaultLocation(event.target.checked)}
              label="Is this location default?"
            />
          </Col>
        </Row>
        <Form.Group controlId="Title">
          <Row>
            <Col md={2}>
              <Form.Label>
                Title:
              </Form.Label>
            </Col>
            <Col md={10}>
              <Form.Control
                required
                size="sm"
                type="text"
                placeholder="Title"
                name="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={isTitleTooLong ? 'text-limit' : null}
              />
              <Form.Text className="char-limit-info-box" style={{ top: 0, bottom: 'auto' }}>
                {Number(limits.scene_title_max.value) - (title ? title.length : 0)}
              </Form.Text>
              <Form.Control.Feedback
                type="invalid"
                className={isTitleTooLong ? 'd-block' : 'd-none'}
              >
                Title is too long
              </Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Please enter Location title
              </Form.Control.Feedback>
            </Col>
          </Row>
        </Form.Group>
        {
          (userRole === 'admin' || videoEnabled || selectedVideo) && (
            <>
              <Row className={classes.videoRow}>
                <Col md={2}>
                  <Form.Label>
                    Video:
                  </Form.Label>
                </Col>
                <Col lg={5} md={7}>
                  <div className={classes.videoField}>
                    {selectedVideo?.title || '-'}
                  </div>
                </Col>
                <Col md="auto">
                  <VideoPicker
                    className={classes.videoPickerButton}
                    title="Select video"
                    initialValue={selectedVideo}
                    onConfirm={handleVideoPickerConfirm}
                    disabled={disabled || !videoEnabled}
                  />

                  {selectedVideo && (
                    <>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleVideoPreviewClick}
                        className={classes.playButton}
                      >
                        <FaPlay />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className={classes.removeButton}
                        onClick={() => setSelectedVideo(null)}
                        disabled={disabled || !videoEnabled}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </Col>
              </Row>
              <Row>
                <Col md={2}>
                  <Form.Label>
                    Video settings:
                  </Form.Label>
                </Col>
                <Col md={10} className={classes.videoSettings}>
                  <div className={classes.loopField}>
                    <Form.Check
                      custom
                      disabled={!selectedVideo || disabled || !videoEnabled}
                      checked={isLoopVideo}
                      id="isLoopVideo"
                      onChange={handleLoopChanged}
                      label="Loop"
                    />
                  </div>
                  <div className={classes.stepActionsField}>
                    <Form.Check
                      custom
                      // We force "Allow Step Actions" to be true if "Loop" is true
                      disabled={!selectedVideo || disabled || !videoEnabled || isLoopVideo}
                      checked={allowStepActions}
                      id="allowStepActions"
                      onChange={(event) => setAllowStepActions(!event.target.checked ? 0 : 1)}
                      label="Allow Step Actions"
                    />
                  </div>
                </Col>
              </Row>
            </>
          )
        }
        {
          userRole === 'admin' && (
            <>
              <Row className={classes.audioRow}>
                <Col md={2}>
                  <Form.Label>
                    [Deprecated] Audio:
                  </Form.Label>
                </Col>
                <Col lg={5} md={7}>
                  <div className={classes.audioField}>
                    {selectedAudio?.title || '-'}
                  </div>
                </Col>
                <Col md="auto">
                  <AudioPicker
                    className={classes.audioPickerButton}
                    title="Select audio"
                    initialValue={selectedAudio}
                    onConfirm={handleAudioPickerConfirm}
                  />

                  {selectedAudio && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className={classes.removeButton}
                    onClick={() => setSelectedAudio(null)}
                  >
                    Remove
                  </Button>
                  )}
                </Col>
              </Row>
              <Row className={classes.audioRow}>
                <Col md={2}>
                  <Form.Label>
                    [Deprecated] Audio settings:
                  </Form.Label>
                </Col>
                <Col md={10} className={classes.audioSettings}>
                  <div className={classes.loopField}>
                    <Form.Check
                      custom
                      disabled={!selectedAudio}
                      checked={isLoopAudio}
                      id="isLoopAudio"
                      onChange={(event) => setIsLoopAudio(event.target.checked)}
                      label="Loop"
                    />
                  </div>
                  <div className={classes.volumeField}>
                    <RangeSlider
                      disabled={!selectedAudio}
                      value={audioVolume}
                      min={0}
                      max={1}
                      step={0.1}
                      size="sm"
                      tooltipLabel={createRangeTooltip}
                      onChange={handleRangeChanged}
                    />
                    <Form.Label className={classes.volumeLabel}>
                      Volume
                    </Form.Label>
                  </div>
                  <Button
                    disabled={!selectedAudio}
                    className={classes.playButton}
                    variant="outline-primary"
                    size="sm"
                    onClick={togglePlayAudio}
                  >
                    {isPlayingAudio
                      ? 'Stop'
                      : ' Listen'}
                  </Button>
                </Col>
              </Row>
            </>
          )
        }
        <Row>
          <Col md={12} className="text-right mb-3">
            <Button
              className="mr-1"
              size="sm"
              type="reset"
              variant="secondary"
              onClick={() => {
                onStopEdit();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={disabled || isTitleTooLong}
              className="ml-1"
              size="sm"
              type="submit"
              variant="primary"
            >
              Save
            </Button>
          </Col>
        </Row>
      </Form>
      <Row>
        <Col md={12} className="text-center mb-1">
          Available locations:
        </Col>
      </Row>
      <Row>
        <Col sm="auto" className="mt-1">
          Filter by:
        </Col>
        <Col className="ml-2">
          <Search value={searchTerm} onChange={handleSearchChange} />
        </Col>
        <Col sm="auto" className="text-right mt-1 ml-3">
          <Form.Check
            type="switch"
            id="showOnlyUserImages"
            label="Show only my Art"
            checked={showOnlyUserImages}
            onChange={(event) => {
              setShowOnlyUserImages(event.target.checked);
              setCurrentPage(1);
            }}
          />
        </Col>
      </Row>
      <Row className={cs(classes.list, 'mb-2')}>
        {isLoading ? (
          <Spinner
            variant="primary"
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
          />
        ) : (
          <LocationsList
            backgrounds={backgrounds}
            selectedImageId={selectedImageId}
            onImageClick={handleImageClick}
          />
        )}
      </Row>
      <Row>
        <Pagination
          className="pagination-bar"
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </Row>
      <VideoModal
        video={selectedVideo}
        isVisible={isVideoModalShown}
        onClose={() => setIsVideoModalShown(false)}
        videoSettings={{
          volume: videoVolume,
          loop: isLoopVideo,
          allowStepActions,
          autoplay: 1,
        }}
      />
      </fieldset>
    </Container>
  );
}
